import type { CommunityPublishValidation } from './community.policy';

export const COMMUNITY_MEMBER_FIELD_TYPES = [
  'text',
  'select',
  'multiselect',
  'checkbox',
  'url',
] as const;

export type CommunityMemberFieldType = (typeof COMMUNITY_MEMBER_FIELD_TYPES)[number];

export const COMMUNITY_MEMBER_FIELD_OPTIONS_MAX = 20;
export const COMMUNITY_MEMBER_FIELD_OPTION_MAX_CHARS = 120;

export type CommunityMemberFieldConditionalRules = {
  showWhenFieldId?: string | null;
  showWhenEquals?: string | null;
};

export type CommunityMemberFieldDefinition = {
  id: string;
  prompt: string;
  required: boolean;
  enabled: boolean;
  fieldType?: CommunityMemberFieldType | string | null;
  options?: string[] | null;
  conditionalRules?: CommunityMemberFieldConditionalRules | null;
};

export function normalizeCommunityMemberFieldType(
  value?: string | null,
): CommunityMemberFieldType {
  const normalized = value?.trim().toLowerCase();
  if (
    normalized &&
    COMMUNITY_MEMBER_FIELD_TYPES.includes(normalized as CommunityMemberFieldType)
  ) {
    return normalized as CommunityMemberFieldType;
  }
  return 'text';
}

export function normalizeCommunityMemberFieldOptions(options?: string[] | null): string[] {
  if (!options?.length) return [];
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const option of options) {
    const trimmed = option.trim();
    if (!trimmed || seen.has(trimmed.toLowerCase())) continue;
    seen.add(trimmed.toLowerCase());
    normalized.push(trimmed.slice(0, COMMUNITY_MEMBER_FIELD_OPTION_MAX_CHARS));
    if (normalized.length >= COMMUNITY_MEMBER_FIELD_OPTIONS_MAX) break;
  }
  return normalized;
}

export function normalizeCommunityMemberFieldConditionalRules(
  rules?: CommunityMemberFieldConditionalRules | null,
): CommunityMemberFieldConditionalRules | null {
  if (!rules) return null;
  const showWhenFieldId = rules.showWhenFieldId?.trim();
  const showWhenEquals = rules.showWhenEquals?.trim();
  if (!showWhenFieldId || !showWhenEquals) return null;
  return { showWhenFieldId, showWhenEquals };
}

export function validateCommunityMemberFieldDefinition(
  field: Pick<CommunityMemberFieldDefinition, 'prompt' | 'fieldType' | 'options'>,
): CommunityPublishValidation {
  if (!field.prompt.trim()) {
    return { ok: false, message: 'Field prompt is required' };
  }

  const fieldType = normalizeCommunityMemberFieldType(field.fieldType);
  const options = normalizeCommunityMemberFieldOptions(field.options);

  if ((fieldType === 'select' || fieldType === 'multiselect') && options.length === 0) {
    return { ok: false, message: 'Select fields require at least one option' };
  }

  return { ok: true };
}

export function isCommunityMemberFieldVisible(
  field: CommunityMemberFieldDefinition,
  answers?: Record<string, string>,
): boolean {
  if (!field.enabled) return false;
  const rules = normalizeCommunityMemberFieldConditionalRules(field.conditionalRules);
  if (!rules) return true;
  const answer = answers?.[rules.showWhenFieldId ?? '']?.trim();
  return answer === rules.showWhenEquals;
}

function isValidUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

export function validateCommunityMemberFieldAnswerValue(
  field: CommunityMemberFieldDefinition,
  rawValue?: string,
  maxAnswerChars = 1000,
): CommunityPublishValidation {
  const fieldType = normalizeCommunityMemberFieldType(field.fieldType);
  const value = rawValue?.trim() ?? '';

  if (!value) {
    return field.required ? { ok: false, message: `Answer required: ${field.prompt}` } : { ok: true };
  }

  if (value.length > maxAnswerChars) {
    return {
      ok: false,
      message: `Answer for "${field.prompt}" must be ${maxAnswerChars} characters or fewer`,
    };
  }

  const options = normalizeCommunityMemberFieldOptions(field.options);

  if (fieldType === 'select' && !options.includes(value)) {
    return { ok: false, message: `Choose a valid option for "${field.prompt}"` };
  }

  if (fieldType === 'multiselect') {
    const selected = value.split(',').map((part) => part.trim()).filter(Boolean);
    if (selected.length === 0) {
      return field.required ? { ok: false, message: `Answer required: ${field.prompt}` } : { ok: true };
    }
    for (const item of selected) {
      if (!options.includes(item)) {
        return { ok: false, message: `Choose valid options for "${field.prompt}"` };
      }
    }
  }

  if (fieldType === 'checkbox' && !['true', 'false', 'yes', 'no', 'on', 'off'].includes(value.toLowerCase())) {
    return { ok: false, message: `Invalid checkbox value for "${field.prompt}"` };
  }

  if (fieldType === 'url' && !isValidUrl(value)) {
    return { ok: false, message: `Enter a valid URL for "${field.prompt}"` };
  }

  return { ok: true };
}

export function validateCommunityMemberFieldAnswersTyped(
  fields: CommunityMemberFieldDefinition[],
  answers: Record<string, string> | undefined,
  maxAnswerChars = 1000,
): CommunityPublishValidation {
  for (const field of fields) {
    if (!isCommunityMemberFieldVisible(field, answers)) continue;
    const validation = validateCommunityMemberFieldAnswerValue(field, answers?.[field.id], maxAnswerChars);
    if (!validation.ok) return validation;
  }
  return { ok: true };
}
